import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useParams } from 'react-router-dom'
import { projectFilesRootGuid } from '@constants'
import { getMemberCheckCategoryLabel, getMemberCheckShortLabel } from '@domainConstants'
import { bundleForElement } from '@domainUtils'
import { loadCategoryToShort } from '@editorUtils'
import {
  makeCrossSectionLabel,
  makeSlabBeamCrossSectionLabel,
  maxUtilizationToMemberPositionBundle,
} from '@editorUtils'
import { useSlabBeamToAssembly } from '@hooks'
import { useIsCommentEscScopeActive } from '@scene'
import { Allotment } from 'allotment'
import { cloneDeep, filter, find, keyBy, maxBy, orderBy, reject } from 'lodash-es'
import { Alert, Box, Button, Stack } from '@mui/material'
import OrthographicControls from '@editor/components/OrthographicControls'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useIsMutating } from '@tanstack/react-query'
import { MaterialSymbol } from '@ui2/icons'
import { publishCornerConnectionProposalEvent } from '@ui/events/cornerConnectionProposal'
import { LoadingIndicator } from '@ui/feedback'
import { NavbarPortalCenter } from '@ui/navigation'
import { useAppStore } from '@stores'
import {
  useControlStore,
  useEditorViewStore,
  useModelStore,
  useResultsStore,
  useStiffeningStore,
  useTapelineStore,
  useTransmissionStore,
} from '@editorStores'
import {
  useResultsSelection,
  useElementLabel,
  useEnterEditorPage,
  useElementCrossSectionAssignment,
  usePositionGuidToWallGuid,
  useProjectFiles,
  useSelectionMode,
  useWallIdToAssembly,
} from '@editorHooks'
import { ColoringControls, StoreyControlsPortal, WidgetPortal } from '@editorComponents'
import { saveManualLoadsPerSupport, savePositionGrouping } from '@mutations'
import {
  formatShearDistance,
  SHEAR_ANCHOR_POSITIONS,
} from 'src/components/manager/shearConnections'
import {
  getSlabBeamCrossSectionFromAssembly,
  getSlabBeamStepSizeFromAssembly,
} from 'src/utils/assemblies'
import useAnchorGridCloseGuard from './components/AnchorDataGrid2/useAnchorGridCloseGuard'
import CornerConnectionProposalForm from './components/CornerConnectionAdditionalLoadProposalForm'
import DetailPane from './components/DetailPane'
import {
  formatNewtonsAsKn,
  isUncriticalAnchorDesignForce,
} from './components/Results2AnchorDetailPane/utils'
import {
  buildMemberCheckSettingSupportsContext,
  getAllowedOverutilizationPercentForCheck,
  type MemberCheckSettingValidationContext,
} from './components/Results2DetailPane/utils'
import type { Results2ListItem } from './components/Results2ElementList'
import Results2ListPane from './components/Results2ListPane'
import Results2SceneLoadTypeSwitches from './components/Results2SceneLoadTypeSwitches'
import useCornerConnectionAdditionalLoadProposalState from './hooks/useCornerConnectionAdditionalLoadProposalState'
import useDesignFormNavigationGuard from './hooks/useDesignFormNavigationGuard'
import { useResults2CameraEvent } from './hooks/useResults2CameraEvent'
import useResults2TabErrorStatuses from './hooks/useResults2TabErrorStatuses'
import useResultsQueries from './hooks/useResultsQueries'
import useShearAnchorChecks from './hooks/useShearAnchorChecks'
import useUpdateCSandChecks from './hooks/useUpdateCSAndChecks'
import {
  ANCHOR_SIDE_SHORT_LABEL,
  SHEAR_ANCHOR_POSITION_SHORT_LABEL,
  getAnchorSides,
  getCommentValue,
  isUtilizationError,
  isUtilizationWarning,
  isVibrationError,
  primaryToResultsTab,
  resultsTabToPrimary,
  resolveResults2ListElementCrossSection,
  resultsTabToSecondary,
  secondaryToWallSubTab,
  shearAnchorLocationOfRowId,
  shearAnchorRowId,
  shearAnchorRowPosition,
  shearAnchorRowSegmentGuid,
} from './utils'

const Results2 = (): ReactElement => {
  useEnterEditorPage('results')
  // Prefetch the element documents ("Dokumente" tab of the detail panes, same query)
  useProjectFiles(projectFilesRootGuid)

  const { projectId } = useParams()
  // The list pane starts at preferredSize 395 (>= 370), so tab labels are shown initially.
  const [showTabLabels, setShowTabLabels] = useState(true)
  const results2FokusCamera = useAppStore(state => state.results2FokusCamera)
  const setResults2FokusCamera = useAppStore(state => state.setResults2FokusCamera)
  const results2FokusTransparency = useAppStore(state => state.results2FokusTransparency)
  const setResults2FokusTransparency = useAppStore(state => state.setResults2FokusTransparency)

  const {
    cornerConnectionState,
    isLoadingProposal,
    abortHelper,
    fetchWithInterval,
    isLoadingWithInterval,
  } = useCornerConnectionAdditionalLoadProposalState()

  const getElementLabel = useElementLabel()
  const wallIdToAssembly = useWallIdToAssembly()
  const positionGuidToWallGuid = usePositionGuidToWallGuid()
  const results2IntegratedAnchorView = useAppStore(state => state.results2IntegratedAnchorView)
  const tab = useEditorViewStore(state => state.results.tab)
  const rawWallSubTab = useEditorViewStore(state => state.results.wallSubTab)
  // The "Anker" tab only exists while the integrated anchor view is enabled; if the setting is
  // turned off while it is active, fall back to Rippen instead of rendering an orphaned tab.
  const wallSubTab =
    rawWallSubTab === 'anchors' && !results2IntegratedAnchorView ? 'wall-rips' : rawWallSubTab
  const guardDesignFormNav = useDesignFormNavigationGuard()
  const isMultiEditMode = useEditorViewStore(state => state.results.isMultiEditMode)
  const setResultsTab = useEditorViewStore(state => state.setResultsTab)
  const setResultsWallSubTab = useEditorViewStore(state => state.setResultsWallSubTab)
  const {
    selectedElements: selectedElementIds,
    selectedStiffeningSegment,
    selectedAnchor,
    selectedAnchors,
    selectedShearAnchor,
    activeMode,
    selectResultsPosition: selectPosition,
    selectResultsSurface: selectSurface,
    selectResultsStiffeningSegment: selectStiffeningSegment,
    selectResultsAnchor: selectAnchor,
    setResultsAnchorSelection: setAnchorSelection,
    selectedShearAnchorPosition,
    selectedShearAnchorLocations,
    selectResultsShearAnchor: selectShearAnchor,
    setResultsShearAnchorSelection: setShearAnchorSelection,
    resetResultsSelection: resetSelectedElements,
    setResultsMode,
    setResultsSelection,
    addResultsSelection,
  } = useResultsSelection()
  const structuralChecks = useResultsStore(state => state.structuralChecks)
  const stiffeningSegmentChecks = useResultsStore(state => state.stiffeningSegmentChecks)
  const standardRipChecks = useResultsStore(state => state.standardRipChecks)
  const memberCheckSettings = useResultsStore(state => state.memberCheckSettings)
  const wallRipsPositionGrouping = useResultsStore(state => state.wallRipsPositionGrouping)
  const wallLintelsPositionGrouping = useResultsStore(state => state.wallLintelsPositionGrouping)
  const slabBeamsPositionGrouping = useResultsStore(state => state.slabBeamsPositionGrouping)
  const roofSlabBeamsPositionGrouping = useResultsStore(
    state => state.roofSlabBeamsPositionGrouping,
  )
  const beamsPositionGrouping = useResultsStore(state => state.beamsPositionGrouping)
  const purlinsPositionGrouping = useResultsStore(state => state.purlinsPositionGrouping)
  const columnsPositionGrouping = useResultsStore(state => state.columnsPositionGrouping)
  const anchorInterventionTableData = useResultsStore(state => state.anchorInterventionTableData)
  const anchorChecks = useResultsStore(state => state.anchorChecks)

  const elementCrossSectionAssignment = useElementCrossSectionAssignment()
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const tensileTransmissionGraph = useTransmissionStore(state => state.tensileTransmissionGraph)
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)
  const walls = useModelStore(state => state.model.walls)
  const verticalSlabsModel = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabsModel = useModelStore(state => state.model.vertical_roof_slabs)
  const foundationElements = useModelStore(state => state.model.foundationElements)
  const setTypeVisibility = useModelStore(state => state.setTypeVisibility)

  // Selection stores the surface (Decke/Dach) guid, never the slab beam guid. These maps
  // convert between the stored surface guid and the beam guid the list rows are keyed by.
  const beamGuidToSurfaceGuid = useMemo(() => {
    const map: Record<string, string> = {}
    for (const slab of [...verticalSlabsModel, ...verticalRoofSlabsModel]) {
      if (slab.beam?.guid) map[slab.beam.guid] = slab.guid
    }
    return map
  }, [verticalSlabsModel, verticalRoofSlabsModel])

  const surfaceGuidToBeamGuid = useMemo(() => {
    const map: Record<string, string> = {}
    for (const slab of [...verticalSlabsModel, ...verticalRoofSlabsModel]) {
      if (slab.beam?.guid) map[slab.guid] = slab.beam.guid
    }
    return map
  }, [verticalSlabsModel, verticalRoofSlabsModel])
  const {
    compute,
    isLoadingResults,
    isLoadingInitial,
    isLoadingResultsWithoutComputing,
    abortLoadingResults,
  } = useResultsQueries(projectId as string)

  const storey = useControlStore(state => state.floorplanState.storey)
  const setFloorplanStorey = useControlStore(state => state.setFloorplanStorey)
  const resultsListCollapsed = useControlStore(state => state.resultsListCollapsed)
  const setIsOrthographic = useCameraStore(state => state.setIsOrthographic)

  const setColoringMode = useControlStore(state => state.setColoringMode)

  const { isSelectionMode, unsetSelectionMode } = useSelectionMode()

  useEffect(() => {
    setColoringMode('groupedElements')
  }, [setColoringMode])

  // Slab beams default hidden globally; show them while Results2 is open and restore on leave.
  useEffect(() => {
    const prevSlabBeams = useModelStore.getState().visibilityByType.slab_beams
    const prevRoofSlabBeams = useModelStore.getState().visibilityByType.roof_slab_beams
    setTypeVisibility('slab_beams', true)
    setTypeVisibility('roof_slab_beams', true)
    return () => {
      setTypeVisibility('slab_beams', prevSlabBeams)
      setTypeVisibility('roof_slab_beams', prevRoofSlabBeams)
    }
  }, [setTypeVisibility])

  const primary = useMemo(() => resultsTabToPrimary(tab), [tab])
  const secondary = useMemo(() => resultsTabToSecondary(tab, wallSubTab), [tab, wallSubTab])

  const { data: shearAnchorChecks } = useShearAnchorChecks()

  // Tint the tab headers (beams/purlins/columns/Decke/Dach and the Wand sub-tabs incl. Anker and
  // Schubanker) when any of their elements is over-utilized (mirrors the old Results view).
  // Derived from the position groupings and check nodes, not the active `listItems`, so every tab
  // reflects its own elements regardless of which one is open.
  const tabErrorStatuses = useResults2TabErrorStatuses(shearAnchorChecks)

  const isStiffeningTab = tab === 'walls' && wallSubTab === 'stiffening'
  const isStandardRipsTab = tab === 'walls' && wallSubTab === 'standard-rips'
  const isAnchorsTab = tab === 'walls' && wallSubTab === 'anchors'
  const isShearAnchorsTab = tab === 'walls' && wallSubTab === 'shear-anchors'
  const isWallAssemblyTab = tab === 'walls' && wallSubTab === 'wall-assemblies'
  const isAssemblySurfaceTab = tab === 'slabs' || tab === 'roof-slabs'
  // Foundations carry no design checks, so they render as a plain, ungrouped list (like Decke/Dach).
  const isFoundationTab = tab === 'foundation'
  const groupingEnabled =
    !isWallAssemblyTab &&
    !isStiffeningTab &&
    !isStandardRipsTab &&
    !isAnchorsTab &&
    !isShearAnchorsTab &&
    !isAssemblySurfaceTab &&
    !isFoundationTab

  // Selecting a wall opens Wand → Wände at the callsite that makes the selection (the scene click
  // handler, useJumpToResultElement, useSelectResults2Element). Deriving it from the selection in
  // an effect instead would also fire on a plain tab switch — since the selection now survives
  // one — and snap the user straight back to Wände.

  const groupingByPrimary = useMemo(() => {
    if (tab === 'slabs' || tab === 'roof-slabs') return null
    if (
      tab === 'walls' &&
      (wallSubTab === 'wall-assemblies' ||
        wallSubTab === 'stiffening' ||
        wallSubTab === 'shear-anchors' ||
        wallSubTab === 'standard-rips')
    ) {
      return null
    }
    if (tab === 'slab-beams') return slabBeamsPositionGrouping
    if (tab === 'roof-slab-beams') return roofSlabBeamsPositionGrouping
    if (tab === 'walls') {
      return wallSubTab === 'wall-lintels' ? wallLintelsPositionGrouping : wallRipsPositionGrouping
    }
    if (tab === 'beams') return beamsPositionGrouping
    if (tab === 'purlins') return purlinsPositionGrouping
    if (tab === 'columns') return columnsPositionGrouping
    return null
  }, [
    tab,
    wallSubTab,
    wallLintelsPositionGrouping,
    wallRipsPositionGrouping,
    slabBeamsPositionGrouping,
    roofSlabBeamsPositionGrouping,
    beamsPositionGrouping,
    purlinsPositionGrouping,
    columnsPositionGrouping,
  ])

  const positionType = useMemo<PositionGroupingType | null>(() => {
    if (tab === 'walls' && wallSubTab === 'wall-lintels') return 'wall-lintels'
    if (tab === 'walls' && wallSubTab === 'wall-rips') return 'wall-rips'
    if (tab === 'slab-beams') return 'slab-beams'
    if (tab === 'roof-slab-beams') return 'roof-slab-beams'
    if (tab === 'beams') return 'beams'
    if (tab === 'purlins') return 'purlins'
    if (tab === 'columns') return 'columns'
    return null
  }, [tab, wallSubTab])

  const typeLabel = useMemo(() => {
    if (tab === 'walls' && wallSubTab === 'anchors') return 'Zuganker'
    if (tab === 'walls' && wallSubTab === 'shear-anchors') return 'Schubanker'
    if (tab === 'walls' && wallSubTab === 'stiffening') return 'Wandscheibe'
    if (tab === 'walls' && wallSubTab === 'standard-rips') return 'Standardrippe'
    if (tab === 'walls' && wallSubTab === 'wall-lintels') return 'Sturz'
    if (tab === 'walls') return 'Rippe'
    if (tab === 'slabs') return 'Decke'
    if (tab === 'roof-slabs') return 'Dach'
    if (tab === 'slab-beams') return 'Deckenbalken'
    if (tab === 'roof-slab-beams') return 'Dachbalken'
    if (tab === 'beams') return 'Unterzug'
    if (tab === 'purlins') return 'Pfette'
    if (tab === 'columns') return 'Stütze'
    if (tab === 'foundation') return 'Fundament'
    return 'Bauteil'
  }, [tab, wallSubTab])

  const assignmentByGuid = useMemo(
    () => keyBy(elementCrossSectionAssignment, 'element_guid'),
    [elementCrossSectionAssignment],
  )

  const slabBeamGuidToAssembly = useSlabBeamToAssembly()
  const { mutateAsync: saveCSAssignment } = useUpdateCSandChecks()
  const [copyParentCrossSectionLoadingChildId, setCopyParentCrossSectionLoadingChildId] = useState<
    string | null
  >(null)

  const listItems = useMemo<Results2ListItem[]>(() => {
    const memberCheckSettingsByElementGuid = keyBy(memberCheckSettings, 'element_guid')

    // Current design context of an element; saved tolerances are ignored when
    // their reference data no longer matches it.
    const settingValidationContextForGuid = (
      guid: string,
      fallbackCrossSection?: CrossSectionTimberOrCLTOrSteel | null,
    ): MemberCheckSettingValidationContext => {
      let crossSection = assignmentByGuid[guid]?.element_cs as
        | CrossSectionTimberOrCLTOrSteel
        | undefined
      if (!crossSection) {
        const assembly = slabBeamGuidToAssembly[guid]
        if (assembly) {
          try {
            crossSection = getSlabBeamCrossSectionFromAssembly(assembly)
          } catch {
            crossSection = undefined
          }
        }
      }
      return {
        crossSection: crossSection ?? fallbackCrossSection,
        supports: buildMemberCheckSettingSupportsContext(guid, verticalTransmissionGraph),
      }
    }

    if (isWallAssemblyTab) {
      const rows: Results2ListItem[] = walls.map(wall => {
        const assembly = wallIdToAssembly[wall.guid]
        const assemblyType =
          assembly?.usage_type === 'Inner'
            ? 'IW'
            : assembly?.usage_type === 'Outer'
            ? 'AW'
            : undefined

        return {
          id: wall.guid,
          label: getElementLabel(wall.guid),
          type: 'Wand',
          qs: [assemblyType, assembly?.name].filter(Boolean).join(' · ') || 'n/a',
          status: 'idle',
          comment: '',
          extended_comment: '',
        }
      })

      return orderBy(rows, ['label'], ['asc'])
    }

    if (isStiffeningTab) {
      const segmentIds = Array.from(
        new Set(stiffeningSegmentChecks.map(check => check.element_guid)),
      )
      const rows = segmentIds.map(segmentId => {
        const checksForSegment = filter(stiffeningSegmentChecks, { element_guid: segmentId })
        const maxCheck = maxBy(checksForSegment, check => check.max_utilization)
        const eta = maxCheck?.max_utilization
        const segment = find(stiffeningIntervals, { guid: segmentId })
        const assembly = segment?.element_guid ? wallIdToAssembly[segment.element_guid] : undefined
        const assemblyType =
          assembly?.usage_type === 'Inner'
            ? 'IW'
            : assembly?.usage_type === 'Outer'
            ? 'AW'
            : undefined

        const status: Results2ListItem['status'] =
          eta === undefined
            ? 'idle'
            : eta >= 1.05
            ? 'err'
            : eta >= 1
            ? 'warn'
            : eta > 0
            ? 'ok'
            : 'idle'

        return {
          id: segmentId,
          label: getElementLabel(segmentId) || segmentId,
          type: 'Wandscheibe',
          qs: assemblyType ?? 'n/a',
          assemblyName: assembly?.name?.trim() || 'n/a',
          eta,
          status,
          comment: '',
          extended_comment: '',
        }
      })

      return orderBy(rows, ['eta', 'label'], ['desc', 'asc'])
    }

    if (isStandardRipsTab) {
      if (!standardRipChecks) return []

      const bundles = standardRipChecks.map(ripCheck => {
        return {
          representative_position: ripCheck.standard_rip_member.guid,
          representative_for: [],
          exported: false,
        } as MemberPositionBundle
      })
      const checks = standardRipChecks.flatMap(ripCheck => ripCheck.standard_rip_checks)
      const bundlesWithUtil = maxUtilizationToMemberPositionBundle(bundles, checks)

      const rows = bundlesWithUtil.map(bundle => {
        const checksForRip = standardRipChecks.filter(
          ripCheck => ripCheck.standard_rip_member.guid === bundle.representative_position,
        )
        const ripCheck = checksForRip.length === 1 ? checksForRip[0] : undefined
        const wallGuid =
          ripCheck?.wall_guid ?? positionGuidToWallGuid[bundle.representative_position]
        const wallAssembly = wallGuid ? wallIdToAssembly[wallGuid] : undefined
        const assemblyType =
          wallAssembly?.usage_type === 'Inner'
            ? 'IW'
            : wallAssembly?.usage_type === 'Outer'
            ? 'AW'
            : undefined
        const crossSectionLabel = ripCheck?.standard_rip_member.cross_section
          ? makeCrossSectionLabel(ripCheck.standard_rip_member.cross_section) ?? 'n/a'
          : 'n/a'
        const eta = bundle.maxUtilWithoutSupportCompression?.max_utilization
        const etaC = bundle.maxUtilOnlySupportCompression?.max_utilization
        const checkSpecificSettings =
          memberCheckSettingsByElementGuid[bundle.representative_position]?.check_specific_settings
        const etaIsVibrationErrorForBundle = isVibrationError(
          bundle.maxUtilWithoutSupportCompression?.check_type,
          bundle.maxUtilWithoutSupportCompression?.max_utilization,
        )
        const settingValidationContext = settingValidationContextForGuid(
          bundle.representative_position,
          ripCheck?.standard_rip_member.cross_section,
        )
        const etaAllowedOverutilizationPercent = getAllowedOverutilizationPercentForCheck(
          bundle.maxUtilWithoutSupportCompression,
          checkSpecificSettings,
          settingValidationContext,
        )
        const etaCAllowedOverutilizationPercent = getAllowedOverutilizationPercentForCheck(
          bundle.maxUtilOnlySupportCompression,
          checkSpecificSettings,
          settingValidationContext,
        )

        const status: Results2ListItem['status'] =
          etaIsVibrationErrorForBundle ||
          (eta !== undefined && isUtilizationError(eta, etaAllowedOverutilizationPercent)) ||
          (etaC !== undefined && isUtilizationError(etaC, etaCAllowedOverutilizationPercent))
            ? 'err'
            : (eta !== undefined && isUtilizationWarning(eta, etaAllowedOverutilizationPercent)) ||
              (etaC !== undefined && isUtilizationWarning(etaC, etaCAllowedOverutilizationPercent))
            ? 'warn'
            : (eta !== undefined && eta > 0) || (etaC !== undefined && etaC > 0)
            ? 'ok'
            : 'idle'

        const assemblyName =
          [assemblyType, wallAssembly?.name?.trim()].filter(Boolean).join(' · ') || 'n/a'

        return {
          id: bundle.representative_position,
          label: wallGuid
            ? getElementLabel(wallGuid)
            : getElementLabel(bundle.representative_position),
          type: 'Standardrippe',
          qs: crossSectionLabel,
          assemblyName,
          eta,
          etaC,
          etaShort: getMemberCheckShortLabel(bundle.maxUtilWithoutSupportCompression?.check_type),
          etaShortTooltip: getMemberCheckCategoryLabel(
            bundle.maxUtilWithoutSupportCompression?.check_type,
          ),
          etaAllowedOverutilizationPercent,
          etaCAllowedOverutilizationPercent,
          etaIsVibrationError: etaIsVibrationErrorForBundle,
          status,
          comment:
            (getCommentValue(ripCheck) ?? getCommentValue(ripCheck?.standard_rip_member)) || '',
          extended_comment: '',
        }
      })

      return orderBy(rows, ['eta', 'etaC', 'label'], ['desc', 'desc', 'asc'])
    }

    if (isShearAnchorsTab) {
      // One row per shear anchor: a stiffening segment is anchored at its foot and at its head,
      // each with its own Schubverbinder and utilization (like the Zuganker tab lists a row per
      // side). The node also holds a wall-level entry (the export lists both), but the anchor is
      // chosen per segment, so only segments are offered here.
      const segmentGuids = new Set(
        (stiffeningIntervals ?? []).map(segment => segment.guid).filter(Boolean),
      )
      const rows: Results2ListItem[] = (shearAnchorChecks ?? [])
        .filter(entry => segmentGuids.has(entry.element_guid))
        .flatMap(entry => {
          const segmentLabel = getElementLabel(entry.element_guid) || entry.element_guid

          return SHEAR_ANCHOR_POSITIONS.map(position => {
            const decisive = maxBy(
              entry.checks.filter(check => check.position === position),
              check => check.utilization,
            )
            const eta = decisive?.utilization

            const status: Results2ListItem['status'] =
              eta === undefined
                ? 'idle'
                : eta >= 1.05
                ? 'err'
                : eta >= 1
                ? 'warn'
                : eta > 0
                ? 'ok'
                : 'idle'

            const selection = entry.selections.find(candidate => candidate.position === position)

            return {
              id: shearAnchorRowId(entry.element_guid, position),
              label: `${segmentLabel} · ${SHEAR_ANCHOR_POSITION_SHORT_LABEL[position]}`,
              type: 'Schubanker',
              shearAnchorPosition: position,
              qs: decisive?.used_anchor.name ?? '-',
              qsManual: selection?.connectorSelectionPolicy === 'UserDefined',
              // Caption under the anchor name: the spacing it is installed at.
              assemblyName: decisive
                ? `Abstand ${formatShearDistance(decisive.used_anchor.distance)} m`
                : undefined,
              eta,
              etaShort: decisive ? loadCategoryToShort[decisive.load_category] : undefined,
              etaShortTooltip: decisive
                ? `Maßgebender Lastfall: ${
                    decisive.load_category === 'EarthQuake' ? 'Erdbeben' : 'Wind'
                  }`
                : undefined,
              status,
              comment: '',
              extended_comment: '',
            }
          })
        })

      return orderBy(rows, ['eta', 'label'], ['desc', 'asc'])
    }

    if (isAnchorsTab) {
      const checkBySupport = keyBy(anchorChecks ?? [], 'element_guid')

      const supportRelativePositions: Record<string, number | undefined> = {}
      for (const support of tensileTransmissionGraph?.element_supports ?? []) {
        supportRelativePositions[support.guid] =
          support.relative_position != null ? parseFloat(support.relative_position) : undefined
      }
      // Stiffening intervals are absolute meters along the wall; the support positions are
      // relative (0..1), so normalize the midpoint by the wall length before comparing.
      const wallLengthByGuid: Record<string, number | undefined> = {}
      for (const wall of walls) {
        const points = wall.shape?.points
        wallLengthByGuid[wall.guid] =
          points && points.length >= 2 ? points[0].distanceTo(points[1]) : undefined
      }
      const segmentMidpoints: Record<string, number | undefined> = {}
      for (const segment of stiffeningIntervals ?? []) {
        if (!segment.guid) continue
        const length = wallLengthByGuid[segment.element_guid]
        segmentMidpoints[segment.guid] = length
          ? (parseFloat(segment.interval.lower) + parseFloat(segment.interval.upper)) / 2 / length
          : undefined
      }
      const sides = getAnchorSides(
        anchorInterventionTableData ?? [],
        supportRelativePositions,
        segmentMidpoints,
      )

      const rows: Results2ListItem[] = (anchorInterventionTableData ?? []).map(row => {
        const check = checkBySupport[row.support_guid]
        const eta = check?.utilization
        // F_d,neu of the decisive load case: the design tensile load over all combos plus the
        // saved manual loads (summed in Newtons, rounded only for display). The detail pane
        // splits the same value per Wind/Erdbeben; the list shows the larger of the two.
        const designForceWithManualLoads =
          row.original_design_force +
          row.manual_loads.reduce((sum, manualLoad) => sum + manualLoad.load, 0)
        const baseLabel = row.segment_guid
          ? getElementLabel(row.segment_guid) || row.segment_guid
          : getElementLabel(row.element_guid) || row.element_guid

        const status: Results2ListItem['status'] =
          eta === undefined
            ? 'idle'
            : eta >= 1.05
            ? 'err'
            : eta >= 1
            ? 'warn'
            : eta > 0
            ? 'ok'
            : 'idle'

        return {
          id: row.support_guid,
          label: `${baseLabel} · ${ANCHOR_SIDE_SHORT_LABEL[sides[row.support_guid] ?? 'links']}`,
          type: 'Zuganker',
          qs: check?.used_anchor?.name ?? '-',
          // Anchors without a chosen product sort to the end of the "gewählter Anker" order.
          qsMissing: !check?.used_anchor?.name,
          qsManual:
            row.connectorSelectionPolicy === 'UserDefined' &&
            check?.used_anchor?.guid === row.userDefinedConnectorId,
          assemblyName: getElementLabel(row.element_guid) || '-',
          // Anchors without a check (e.g. overpressed side) show η = 0 instead of an empty cell.
          eta: eta ?? 0,
          etaShort: check ? loadCategoryToShort[check.load_category ?? 'Wind'] : undefined,
          etaShortTooltip: check
            ? `Maßgebender Lastfall: ${check.load_category === 'EarthQuake' ? 'Erdbeben' : 'Wind'}`
            : undefined,
          trailingMetric: {
            value: formatNewtonsAsKn(designForceWithManualLoads),
            unit: 'kN',
            sortValue: designForceWithManualLoads,
            highlight: isUncriticalAnchorDesignForce(designForceWithManualLoads)
              ? 'success'
              : undefined,
            // Spells the value out too: the column is sized for the common case, so a very
            // large force is only fully readable here.
            tooltip: `F_d,neu = ${formatNewtonsAsKn(
              designForceWithManualLoads,
            )} kN — maßgebende Design-Zuglast inklusive der manuellen Lasten`,
          },
          status,
          comment: row.comment || '',
          extended_comment: '',
        }
      })

      // Same order the list's default sort applies, so the two agree before the user sorts.
      return orderBy(
        rows,
        [item => item.eta ?? 0, item => item.trailingMetric?.sortValue ?? 0, 'label'],
        ['desc', 'desc', 'asc'],
      )
    }

    // Surfaces have no position grouping of their own: their Exportiert flag and Positionslisten
    // texts live on the bundle of the surface's slab beam, and are written back to it.
    if (tab === 'slabs') {
      const rows: Results2ListItem[] = verticalSlabsModel.map(slab => {
        const assembly = wallIdToAssembly[slab.guid]
        const bundle = bundleForElement(slabBeamsPositionGrouping, slab.beam?.guid)
        return {
          id: slab.guid,
          label: getElementLabel(slab.guid),
          type: 'Decke',
          qs: assembly?.name ?? 'n/a',
          etaNotApplicable: true,
          status: 'idle' as const,
          ok: Boolean(bundle?.exported),
          comment: bundle?.comment ?? '',
          extended_comment: bundle?.extended_comment ?? '',
        }
      })
      return orderBy(rows, ['label'], ['asc'])
    }

    if (tab === 'roof-slabs') {
      const rows: Results2ListItem[] = verticalRoofSlabsModel.map(slab => {
        const assembly = wallIdToAssembly[slab.guid]
        const bundle = bundleForElement(roofSlabBeamsPositionGrouping, slab.beam?.guid)
        return {
          id: slab.guid,
          label: getElementLabel(slab.guid),
          type: 'Dach',
          qs: assembly?.name ?? 'n/a',
          etaNotApplicable: true,
          status: 'idle',
          ok: Boolean(bundle?.exported),
          comment: bundle?.comment ?? '',
          extended_comment: bundle?.extended_comment ?? '',
        }
      })
      return orderBy(rows, ['label'], ['asc'])
    }

    if (tab === 'foundation') {
      // Foundations have no assembly and no design checks: show only the label with a "-" instead
      // of an assembly (mirrors the Wände list), so `qs` is a plain dash.
      const rows: Results2ListItem[] = foundationElements.map(foundation => ({
        id: foundation.guid,
        label: getElementLabel(foundation.guid),
        type: 'Fundament',
        qs: '-',
        status: 'idle' as const,
        comment: '',
        extended_comment: '',
      }))
      return orderBy(rows, ['label'], ['asc'])
    }

    if (!groupingByPrimary || !structuralChecks) return []
    const groupingWithUtil = maxUtilizationToMemberPositionBundle(
      groupingByPrimary,
      structuralChecks,
    )

    const rows: Results2ListItem[] = []
    for (const bundle of groupingWithUtil) {
      const parentGuid = bundle.representative_position
      const allGuids = [parentGuid, ...bundle.representative_for]
      let parentQs: string | undefined

      for (const guid of allGuids) {
        const checksForGuid = structuralChecks.filter(check => check.element_guid === guid)
        const supportChecks = checksForGuid.filter(check =>
          ['SupportCompression', 'SteelSupportCompression'].includes(check.check_type),
        )
        const nonSupportChecks = reject(checksForGuid, check =>
          ['SupportCompression', 'SteelSupportCompression'].includes(check.check_type),
        )

        const maxSupportCheck = maxBy(supportChecks, check => check.max_utilization)
        const maxNonSupportCheck = maxBy(nonSupportChecks, check => check.max_utilization)
        const maxAnyCheck = maxBy(checksForGuid, check => check.max_utilization)
        const etaSourceCheck = maxNonSupportCheck ?? maxAnyCheck
        const eta = etaSourceCheck?.max_utilization
        const etaC = maxSupportCheck?.max_utilization
        const checkSpecificSettings =
          memberCheckSettingsByElementGuid[guid]?.check_specific_settings
        // A failed vibration check (η = 1.0) can be outranked by a higher non-support check, so
        // scan every check rather than only the max one (kept in sync with the tab error hook).
        const etaIsVibrationErrorForGuid = checksForGuid.some(check =>
          isVibrationError(check.check_type, check.max_utilization),
        )
        const settingValidationContext = settingValidationContextForGuid(guid)
        const etaAllowedOverutilizationPercent = getAllowedOverutilizationPercentForCheck(
          etaSourceCheck,
          checkSpecificSettings,
          settingValidationContext,
        )
        const etaCAllowedOverutilizationPercent = getAllowedOverutilizationPercentForCheck(
          maxSupportCheck,
          checkSpecificSettings,
          settingValidationContext,
        )

        const status: Results2ListItem['status'] =
          etaIsVibrationErrorForGuid ||
          (eta !== undefined && isUtilizationError(eta, etaAllowedOverutilizationPercent)) ||
          (etaC !== undefined && isUtilizationError(etaC, etaCAllowedOverutilizationPercent))
            ? 'err'
            : (eta !== undefined && isUtilizationWarning(eta, etaAllowedOverutilizationPercent)) ||
              (etaC !== undefined && isUtilizationWarning(etaC, etaCAllowedOverutilizationPercent))
            ? 'warn'
            : eta !== undefined && eta > 0
            ? 'ok'
            : 'idle'

        const assignment = assignmentByGuid[guid]
        let qs: string
        if (tab === 'slab-beams' || tab === 'roof-slab-beams') {
          const assembly = slabBeamGuidToAssembly[guid]
          if (assembly) {
            try {
              const crossSection = getSlabBeamCrossSectionFromAssembly(assembly)
              const stepSize = getSlabBeamStepSizeFromAssembly(assembly)
              qs =
                makeSlabBeamCrossSectionLabel(crossSection, stepSize) ??
                makeCrossSectionLabel(crossSection) ??
                'n/a'
            } catch {
              qs = assignment?.element_cs
                ? makeCrossSectionLabel(assignment.element_cs) ?? 'n/a'
                : 'n/a'
            }
          } else {
            qs = assignment?.element_cs
              ? makeCrossSectionLabel(assignment.element_cs) ?? 'n/a'
              : 'n/a'
          }
        } else {
          qs = assignment?.element_cs
            ? makeCrossSectionLabel(assignment.element_cs) ?? 'n/a'
            : 'n/a'
        }
        const label = getElementLabel(guid)
        const isParent = guid === parentGuid
        if (isParent) parentQs = qs

        rows.push({
          id: guid,
          label,
          type: typeLabel,
          qs,
          eta,
          etaC,
          etaShort: getMemberCheckShortLabel(etaSourceCheck?.check_type),
          etaShortTooltip: getMemberCheckCategoryLabel(etaSourceCheck?.check_type),
          etaAllowedOverutilizationPercent,
          etaCAllowedOverutilizationPercent,
          etaIsVibrationError: etaIsVibrationErrorForGuid,
          status,
          comment: bundle.comment || '',
          extended_comment: bundle.extended_comment || '',
          ok: isParent ? bundle.exported : false,
          bundleExported: bundle.exported,
          parent: isParent ? undefined : parentGuid,
          qsDiffersFromParent: !isParent && parentQs != null && qs !== parentQs,
        })
      }
    }

    // stable tree-like order: parents followed by children as provided by grouping
    return rows
  }, [
    tab,
    verticalSlabsModel,
    verticalRoofSlabsModel,
    foundationElements,
    isWallAssemblyTab,
    walls,
    isStiffeningTab,
    isStandardRipsTab,
    isAnchorsTab,
    isShearAnchorsTab,
    shearAnchorChecks,
    anchorChecks,
    anchorInterventionTableData,
    tensileTransmissionGraph,
    stiffeningSegmentChecks,
    stiffeningIntervals,
    standardRipChecks,
    positionGuidToWallGuid,
    wallIdToAssembly,
    getElementLabel,
    groupingByPrimary,
    slabBeamsPositionGrouping,
    roofSlabBeamsPositionGrouping,
    memberCheckSettings,
    structuralChecks,
    assignmentByGuid,
    slabBeamGuidToAssembly,
    verticalTransmissionGraph,
    typeLabel,
  ])

  const resolvedSelectedId = useMemo(() => {
    if (tab === 'walls' && wallSubTab === 'anchors') {
      if (selectedAnchor && listItems.some(item => item.id === selectedAnchor)) {
        return selectedAnchor
      }
      return null
    }
    if (tab === 'walls' && wallSubTab === 'shear-anchors') {
      // Rows are keyed per anchor (segment and position); the selection is the segment.
      if (
        selectedShearAnchor &&
        listItems.some(item => shearAnchorRowSegmentGuid(item.id) === selectedShearAnchor)
      ) {
        return selectedShearAnchor
      }
      return null
    }
    if (tab === 'walls' && wallSubTab === 'stiffening') {
      if (
        selectedStiffeningSegment &&
        listItems.some(item => item.id === selectedStiffeningSegment)
      ) {
        return selectedStiffeningSegment
      }
      return null
    }
    for (const id of selectedElementIds) {
      // The selection holds the surface guid for slabs/roofs; rows are keyed by the beam.
      const listGuid = surfaceGuidToBeamGuid[id] ?? id
      if (listItems.some(item => item.id === listGuid)) return id
    }
    return null
  }, [
    listItems,
    selectedAnchor,
    selectedShearAnchor,
    selectedElementIds,
    selectedStiffeningSegment,
    surfaceGuidToBeamGuid,
    tab,
    wallSubTab,
  ])

  // The camera hook frames slab/roof beams by their beam guid, so map the stored surface guid
  // back to the beam guid; non-slab selections pass through unchanged. Anchor selections are
  // framed via their stiffening segment (or carrying element) since a support has no geometry.
  const selectedAnchorRow = isAnchorsTab
    ? find(anchorInterventionTableData ?? [], { support_guid: resolvedSelectedId ?? '' })
    : undefined
  const cameraFocusGuid = selectedAnchorRow
    ? selectedAnchorRow.segment_guid ?? selectedAnchorRow.element_guid
    : resolvedSelectedId
    ? surfaceGuidToBeamGuid[resolvedSelectedId] ?? resolvedSelectedId
    : undefined
  useResults2CameraEvent(cameraFocusGuid, results2FokusCamera)

  const handleSelect = useCallback(
    (id: string): boolean => {
      // Slab/roof beam rows store their surface guid as the selection, never the beam guid.
      const selectionGuid = beamGuidToSurfaceGuid[id] ?? id
      if (selectionGuid !== resolvedSelectedId && guardDesignFormNav()) return false

      if (tab === 'walls') {
        if (wallSubTab === 'wall-assemblies') {
          selectPosition(id, 'walls', 'wall-assemblies')
          return true
        }
        if (wallSubTab === 'stiffening') {
          selectStiffeningSegment(id)
          return true
        }
        if (wallSubTab === 'anchors') {
          selectAnchor(id)
          return true
        }
        if (wallSubTab === 'shear-anchors') {
          // A list row names its position; a scene click passes the plain segment guid.
          selectShearAnchor(shearAnchorRowSegmentGuid(id), shearAnchorRowPosition(id))
          return true
        }

        selectPosition(
          id,
          'walls',
          wallSubTab === 'standard-rips'
            ? 'standard-rips'
            : wallSubTab === 'wall-lintels'
            ? 'wall-lintels'
            : 'wall-rips',
        )
        return true
      }

      if (tab === 'slabs') {
        // Selecting a Decke opens its Deckenbalken with the merged "Decke" tab open.
        const beamGuid = find(verticalSlabsModel, { guid: id })?.beam?.guid
        if (beamGuid) selectSurface(id, 'slab-beams')
        else selectPosition(id, 'slabs')
        return true
      }
      if (tab === 'roof-slabs') {
        // Selecting a Dach opens its Dachbalken with the merged "Dach" tab open.
        const beamGuid = find(verticalRoofSlabsModel, { guid: id })?.beam?.guid
        if (beamGuid) selectSurface(id, 'roof-slab-beams')
        else selectPosition(id, 'roof-slabs')
        return true
      }
      // Beam rows: store the surface guid (selectionGuid), keep the beam list tab active.
      if (tab === 'slab-beams') selectPosition(selectionGuid, 'slab-beams')
      else if (tab === 'roof-slab-beams') selectPosition(selectionGuid, 'roof-slab-beams')
      else if (tab === 'beams') selectPosition(id, 'beams')
      else if (tab === 'purlins') selectPosition(id, 'purlins')
      else if (tab === 'columns') selectPosition(id, 'columns')
      else if (tab === 'foundation') selectPosition(id, 'foundation')
      return true
    },
    [
      beamGuidToSurfaceGuid,
      guardDesignFormNav,
      resolvedSelectedId,
      selectPosition,
      selectSurface,
      selectStiffeningSegment,
      selectAnchor,
      selectShearAnchor,
      tab,
      wallSubTab,
      verticalSlabsModel,
      verticalRoofSlabsModel,
    ],
  )

  const handleSelectionChange = useCallback(
    (ids: string[]): boolean => {
      if (ids.length === 0) {
        if (guardDesignFormNav()) return false
        resetSelectedElements()
        return true
      }

      if (ids.length === 1) {
        return handleSelect(ids[0])
      }

      if (guardDesignFormNav()) return false

      // Anchors are supports, not model elements — they keep their own selection.
      if (isAnchorsTab) {
        setAnchorSelection(ids)
        return true
      }
      if (isShearAnchorsTab) {
        setShearAnchorSelection(ids.map(shearAnchorLocationOfRowId))
        return true
      }

      // Beam rows store their surface guid; other rows store their own guid.
      setResultsSelection(ids.map(id => beamGuidToSurfaceGuid[id] ?? id))
      return true
    },
    [
      beamGuidToSurfaceGuid,
      guardDesignFormNav,
      handleSelect,
      isAnchorsTab,
      isShearAnchorsTab,
      resetSelectedElements,
      setAnchorSelection,
      setShearAnchorSelection,
      setResultsSelection,
    ],
  )

  const copyParentCrossSectionVisibleByChildId = useMemo(() => {
    const visible: Record<string, boolean> = {}
    for (const item of listItems) {
      if (!item.parent) continue
      const parentItem = find(listItems, { id: item.parent })
      if (parentItem != null && parentItem.qs === item.qs) continue
      const parentCS = resolveResults2ListElementCrossSection(
        item.parent,
        tab,
        assignmentByGuid,
        slabBeamGuidToAssembly,
      )
      const childAssignment = assignmentByGuid[item.id]
      if (parentCS && childAssignment?.element_cs) {
        visible[item.id] = true
      }
    }
    return visible
  }, [assignmentByGuid, listItems, slabBeamGuidToAssembly, tab])

  const handleCopyParentCrossSection = useCallback(
    async (childId: string, parentId: string) => {
      const parentCS = resolveResults2ListElementCrossSection(
        parentId,
        tab,
        assignmentByGuid,
        slabBeamGuidToAssembly,
      )
      if (!parentCS) return

      const currentAssignment = assignmentByGuid[childId]
      if (!currentAssignment?.element_cs) return

      const parentItem = find(listItems, { id: parentId })
      const childItem = find(listItems, { id: childId })
      if (parentItem && childItem && parentItem.qs === childItem.qs) return

      setCopyParentCrossSectionLoadingChildId(childId)
      try {
        await saveCSAssignment({
          newAssignment: {
            element_guid: childId,
            element_cs: cloneDeep(parentCS) as CrossSection,
          },
          currentCS: currentAssignment.element_cs,
        })
      } finally {
        setCopyParentCrossSectionLoadingChildId(null)
      }
    },
    [assignmentByGuid, listItems, saveCSAssignment, slabBeamGuidToAssembly, tab],
  )

  // The selected shear anchor's row, which the selection names by segment and position.
  const selectedShearAnchorRowId =
    isShearAnchorsTab && resolvedSelectedId && selectedShearAnchorPosition
      ? shearAnchorRowId(resolvedSelectedId, selectedShearAnchorPosition)
      : undefined

  const selectedItem = useMemo(() => {
    if (selectedShearAnchorRowId) return find(listItems, { id: selectedShearAnchorRowId })
    // resolvedSelectedId is the stored surface guid for slabs/roofs; rows are beam-keyed.
    const listGuid = resolvedSelectedId
      ? surfaceGuidToBeamGuid[resolvedSelectedId] ?? resolvedSelectedId
      : undefined
    return find(listItems, item => item.id === listGuid)
  }, [listItems, resolvedSelectedId, selectedShearAnchorRowId, surfaceGuidToBeamGuid])

  const handleCalculate = useCallback(() => {
    if (!projectId) return
    compute(projectId)
  }, [compute, projectId])

  // Abort is available during the cancellable query-loading phase (mirrors the V1 Results page).
  // The compute mutation itself isn't cancellable, so we don't surface abort while only computing.
  const canAbort = isLoadingInitial || isLoadingResultsWithoutComputing

  const selectedStiffeningChecks = useMemo(
    () =>
      isStiffeningTab && resolvedSelectedId
        ? filter(stiffeningSegmentChecks, { element_guid: resolvedSelectedId })
        : [],
    [isStiffeningTab, resolvedSelectedId, stiffeningSegmentChecks],
  )

  const selectedShearAnchorEntry = useMemo(
    () =>
      isShearAnchorsTab && resolvedSelectedId
        ? find(shearAnchorChecks ?? [], { element_guid: resolvedSelectedId })
        : undefined,
    [isShearAnchorsTab, resolvedSelectedId, shearAnchorChecks],
  )
  const selectedShearAnchorChecks = selectedShearAnchorEntry?.checks ?? []
  const selectedShearAnchorLoads = selectedShearAnchorEntry?.loads
  const selectedShearAnchorSelections = selectedShearAnchorEntry?.selections

  const selectedIdsForList = useMemo(() => {
    // Anchor rows are keyed by their support guid and have their own (multi-)selection.
    if (isAnchorsTab) return selectedAnchors
    if (isShearAnchorsTab) {
      return selectedShearAnchorLocations.map(({ guid, position }) =>
        shearAnchorRowId(guid, position),
      )
    }
    // The list rows are beam-keyed, so map the stored surface guids back to their beam guids
    // for highlighting (multi-select highlighting also works in grouping-disabled views).
    return (groupingEnabled || isWallAssemblyTab || isAssemblySurfaceTab) &&
      selectedElementIds.length > 0
      ? selectedElementIds.map(id => surfaceGuidToBeamGuid[id] ?? id)
      : resolvedSelectedId
      ? [surfaceGuidToBeamGuid[resolvedSelectedId] ?? resolvedSelectedId]
      : []
  }, [
    groupingEnabled,
    isAnchorsTab,
    isShearAnchorsTab,
    isWallAssemblyTab,
    isAssemblySurfaceTab,
    resolvedSelectedId,
    selectedAnchors,
    selectedElementIds,
    selectedShearAnchorLocations,
    surfaceGuidToBeamGuid,
  ])
  // The anchors selected in the list (only rows the list actually holds — a filtered-out anchor
  // is not highlighted and must not be edited from the multi-edit pane either).
  const selectedAnchorItems = useMemo(
    () => (isAnchorsTab ? listItems.filter(item => selectedAnchors.includes(item.id)) : []),
    [isAnchorsTab, listItems, selectedAnchors],
  )
  // The same for the Schubanker list, whose rows are keyed by segment and position.
  const selectedShearAnchorItems = useMemo(() => {
    if (!isShearAnchorsTab) return []
    const rowIds = new Set(
      selectedShearAnchorLocations.map(({ guid, position }) => shearAnchorRowId(guid, position)),
    )
    return listItems.filter(item => rowIds.has(item.id))
  }, [isShearAnchorsTab, listItems, selectedShearAnchorLocations])

  const noDataMessage = 'Keine Daten verfügbar'
  const showNoData = !isLoadingResults && listItems.length === 0

  const handlePrimaryChange = useCallback(
    (value: string) => {
      if (guardDesignFormNav()) return
      const nextTab = primaryToResultsTab(value as typeof tab)
      setResultsTab(nextTab, nextTab === 'walls' ? wallSubTab : undefined)
    },
    [guardDesignFormNav, setResultsTab, wallSubTab],
  )

  const handleSecondaryChange = useCallback(
    (value: string) => {
      if (guardDesignFormNav()) return
      // Only walls still have a secondary tab strip.
      if (primary === 'walls') {
        setResultsWallSubTab(secondaryToWallSubTab(value))
      }
    },
    [guardDesignFormNav, primary, setResultsWallSubTab],
  )

  const handlePaneSizeChange = useCallback((sizes: number[]) => {
    // Allotment fires `onChange` continuously while dragging. The list pane width only drives
    // `showTabLabels`, so collapse that to a single boolean and re-render only when the 370px
    // threshold is actually crossed instead of on every drag tick.
    const next = (sizes[0] ?? 0) >= 370
    setShowTabLabels(current => (current === next ? current : next))
  }, [])

  const smartSuggestions = useControlStore(state => state.smartSuggestions)

  const guardAnchorGridClose = useAnchorGridCloseGuard()

  const escAction = useCallback(() => {
    // Blur active form field
    const active = document.activeElement
    if (!(active instanceof HTMLElement)) return
    const tag = active.tagName
    const isFormField =
      tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable
    if (isFormField) active.blur()

    const enabledSmartSuggestions = smartSuggestions.filter(suggestion => suggestion.enabled)
    if (enabledSmartSuggestions.length > 0) {
      // esc is then handled by the smart suggestions controller
      return
    }

    if (cornerConnectionState.type !== 'idle') {
      abortHelper()
      return
    }

    if (activeMode) {
      if (guardAnchorGridClose()) return
      setResultsMode(null)
      return
    }

    if (isSelectionMode) {
      unsetSelectionMode()
      return
    }

    if (
      selectedElementIds.length === 0 &&
      !selectedStiffeningSegment &&
      selectedAnchors.length === 0 &&
      selectedShearAnchorLocations.length === 0
    )
      return

    if (guardDesignFormNav()) return

    resetSelectedElements()
  }, [
    smartSuggestions,
    cornerConnectionState.type,
    activeMode,
    isSelectionMode,
    selectedElementIds.length,
    selectedStiffeningSegment,
    selectedAnchors.length,
    selectedShearAnchorLocations.length,
    guardDesignFormNav,
    resetSelectedElements,
    abortHelper,
    setResultsMode,
    unsetSelectionMode,
    guardAnchorGridClose,
  ])

  const isTapelineDrawing = useTapelineStore(state => state.isDrawing)
  const actionMode = useControlStore(state => state.actionMode)
  const commentEscActive = useIsCommentEscScopeActive()

  useHotkeys(
    'esc',
    escAction,
    {
      enabled: !isTapelineDrawing && actionMode !== 'hide' && !commentEscActive,
      enableOnFormTags: true,
      enableOnContentEditable: true,
    },
    [escAction],
  )

  const elementListGroupingDirty = useResultsStore(state => state.elementListGroupingDirty)
  const isSavingPositionGrouping =
    useIsMutating({
      mutationKey: savePositionGrouping.getKey(projectId as string, positionType).slice(0, 2),
    }) > 0
  // The anchor list's comment autosave replays every support's manual loads (keyed mutation, set
  // only by that autosave). Counting it keeps the anchor detail form disabled while a flushed
  // save is still in flight after the list unmounted — manual-load edits must not race it.
  const isSavingAnchorComments =
    useIsMutating({
      mutationKey: saveManualLoadsPerSupport.getKey(projectId as string),
    }) > 0
  const disableDetailForm =
    elementListGroupingDirty || isSavingPositionGrouping || isSavingAnchorComments

  const disableElementListForm =
    useIsMutating({
      mutationKey: ['saveDetailForm'] as const,
    }) > 0

  const drawerContent = useMemo(() => {
    // In the "Anker" tab the corner-connection helper renders inline in the anchor detail pane,
    // so the panes must stay mounted instead of being replaced by the full-pane helper.
    if (cornerConnectionState.type !== 'idle' && !isAnchorsTab) {
      return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {!!isLoadingProposal && <LoadingIndicator />}
          {(cornerConnectionState.type === 'selected-anchor-and-wall' ||
            cornerConnectionState.type === 'selected-anchor') && (
            <Box m={2}>
              <Alert severity="info">Noch kein Vorschlag erstellt</Alert>
            </Box>
          )}
          {(cornerConnectionState.type === 'proposal-created' ||
            cornerConnectionState.type === 'proposal-changed') && (
            <CornerConnectionProposalForm
              anchorGuid={cornerConnectionState.anchorGuid}
              wallGuid={cornerConnectionState.wallGuid}
              initialData={cornerConnectionState.proposal}
              fetchWithInterval={fetchWithInterval}
              isLoadingWithInterval={isLoadingWithInterval}
              onAccept={async (
                formula: string,
                value: string,
                rawFormula: string,
                maxForce: number,
              ) => {
                if (
                  cornerConnectionState.type !== 'proposal-created' &&
                  cornerConnectionState.type !== 'proposal-changed'
                )
                  return

                publishCornerConnectionProposalEvent({
                  anchorGuid: cornerConnectionState.anchorGuid,
                  manualLoadField: cornerConnectionState.field,
                  rawFormula,
                  maxForce,
                  value,
                })

                abortHelper()
              }}
            />
          )}

          <Box sx={{ mt: 'auto', p: 2 }}>
            <Button
              startIcon={<MaterialSymbol icon="arrow_back" size={20} />}
              onClick={abortHelper}
              variant="outlined"
              data-cy="global-view-btn"
            >
              Eingabehilfe schließen
            </Button>
          </Box>
        </Box>
      )
    }
    return null
  }, [
    abortHelper,
    cornerConnectionState,
    fetchWithInterval,
    isAnchorsTab,
    isLoadingProposal,
    isLoadingWithInterval,
  ])

  return (
    <>
      <StoreyControlsPortal>
        <Results2SceneLoadTypeSwitches />
      </StoreyControlsPortal>

      <WidgetPortal>
        <Stack direction="row" spacing={1}>
          <ColoringControls
            onSelectElements={ids => {
              if (guardDesignFormNav()) return
              // Store surface guids for slab/roof beams, never the beam guids.
              addResultsSelection(ids.map(id => beamGuidToSurfaceGuid[id] ?? id))
            }}
          />

          <OrthographicControls
            isOrthographic={!!storey}
            setIsOrthographic={ortho => {
              if (ortho) {
                setFloorplanStorey('0')
                setIsOrthographic(true)
              } else {
                setFloorplanStorey(null)
                setIsOrthographic(false)
              }
            }}
          />
        </Stack>
      </WidgetPortal>

      <NavbarPortalCenter>
        <div />
      </NavbarPortalCenter>

      <Box className="results2-root" sx={{ height: '100%' }}>
        {drawerContent ? (
          (drawerContent as ReactElement)
        ) : (
          <Allotment onChange={handlePaneSizeChange}>
            <Allotment.Pane
              minSize={300}
              preferredSize={395}
              maxSize={1100}
              visible={!resultsListCollapsed}
            >
              <Box height="100%">
                <Results2ListPane
                  items={listItems}
                  selectedId={selectedShearAnchorRowId ?? resolvedSelectedId ?? undefined}
                  selectedIds={selectedIdsForList}
                  onSelect={handleSelect}
                  onSelectionChange={handleSelectionChange}
                  primary={primary}
                  onPrimaryChange={handlePrimaryChange}
                  secondary={secondary}
                  onSecondaryChange={handleSecondaryChange}
                  showTabLabels={showTabLabels}
                  tabErrorStatuses={tabErrorStatuses}
                  isStiffeningTab={isStiffeningTab}
                  isStandardRipsTab={isStandardRipsTab}
                  isAssemblySurfaceTab={isAssemblySurfaceTab}
                  isAnchorsTab={isAnchorsTab}
                  isShearAnchorsTab={isShearAnchorsTab}
                  groupingEnabled={groupingEnabled}
                  isLoading={isLoadingResults}
                  emptyMessage={noDataMessage}
                  onCalculate={handleCalculate}
                  onAbort={abortLoadingResults}
                  showAbort={canAbort}
                  onCopyParentCrossSection={handleCopyParentCrossSection}
                  copyParentCrossSectionVisibleByChildId={copyParentCrossSectionVisibleByChildId}
                  copyParentCrossSectionLoadingChildId={copyParentCrossSectionLoadingChildId}
                  fokusCamera={results2FokusCamera}
                  onFokusCameraChange={setResults2FokusCamera}
                  fokusTransparency={results2FokusTransparency}
                  onFokusTransparencyChange={setResults2FokusTransparency}
                  disableForm={disableElementListForm}
                />
              </Box>
            </Allotment.Pane>

            <Allotment.Pane minSize={500} preferredSize={650}>
              <Box height="100%" overflow="auto">
                <DetailPane
                  showNoData={showNoData}
                  noDataMessage={noDataMessage}
                  isLoading={isLoadingResults}
                  resolvedSelectedId={resolvedSelectedId ?? undefined}
                  selectedItem={selectedItem}
                  groupingEnabled={groupingEnabled}
                  positionGrouping={groupingByPrimary}
                  positionGroupingType={positionType}
                  isMultiEditMode={isMultiEditMode}
                  selectedElementIds={selectedElementIds}
                  selectedAnchorItems={selectedAnchorItems}
                  selectedShearAnchorItems={selectedShearAnchorItems}
                  resetSelectedElements={resetSelectedElements}
                  isStiffeningTab={isStiffeningTab}
                  isStandardRipsTab={isStandardRipsTab}
                  isAnchorsTab={isAnchorsTab}
                  isShearAnchorsTab={isShearAnchorsTab}
                  isWallAssemblyTab={isWallAssemblyTab}
                  isAssemblySurfaceTab={isAssemblySurfaceTab}
                  isFoundationTab={isFoundationTab}
                  selectedStiffeningChecks={selectedStiffeningChecks}
                  selectedShearAnchorPosition={selectedShearAnchorPosition ?? undefined}
                  selectedShearAnchorChecks={selectedShearAnchorChecks}
                  selectedShearAnchorLoads={selectedShearAnchorLoads}
                  selectedShearAnchorSelections={selectedShearAnchorSelections}
                  isCommentsTab={primary === 'comments'}
                  disableForm={disableDetailForm}
                />
              </Box>
            </Allotment.Pane>
          </Allotment>
        )}
      </Box>
    </>
  )
}

export default Results2
